/**
 * Акти звірки — список (канон Норми #29, 2026-08-22): .card +
 * InfiniteDocGrid (серверна пагінація/сортування), фільтри рядком під
 * назвою — усі виконуються сервером (орг/контрагент/період/стан).
 * Опція «Усі організації» — organization_id не передається, сервер віддає
 * всі доступні юрособи.
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { reconciliationActs, type ReconciliationAct } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { pickDefaultOrg, useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const PERMISSION = 'documents.reconciliation_acts.create';

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Чернетка',
  SENT: 'Відправлено',
  AGREED: 'Узгоджено',
  DISPUTED: 'Спірний',
};

export default function ReconciliationActsPage() {
  const navigate = useNavigate();
  const refreshRef = useRef<(() => void) | null>(null);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );
  // Орг слідує за шапкою до ручного вибору; дати — з верхнього періоду
  // (зауваження власника 2026-08-19). Пусто = «Усі організації».
  const [orgId, setOrgId] = useOrgFilter();
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const [fltContractor, setFltContractor] = useState('');
  const [fltFrom, setFltFrom] = useState(gFrom?.slice(0, 10) ?? '');
  const [fltTo, setFltTo] = useState(gTo?.slice(0, 10) ?? '');
  const [fltState, setFltState] = useState('');
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgList.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgList.data]);
  const contractorName = useMemo(() => {
    const m: Record<string, string> = {};
    (contractorsQ.data?.items ?? []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [contractorsQ.data]);

  const { datasource, total } = useInfiniteGridDatasource<ReconciliationAct>(
    (limit, offset, sort) => reconciliationActs.list({
      organization_id: orgId || undefined,
      contractor_id: fltContractor || undefined,
      state: (fltState || undefined) as ReconciliationAct['state'] | undefined,
      date_from: fltFrom || undefined,
      date_to: fltTo || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, fltContractor, fltState, fltFrom, fltTo],
    { attachmentsType: 'doc.reconciliation_acts' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<ReconciliationAct>('doc.reconciliation_acts', orgId);

  const cols = useMemo<ColDef<ReconciliationAct>[]>(() => [
    {
      field: 'document_number', headerName: 'Номер', width: 130,
      cellRenderer: (p: { data?: ReconciliationAct }) => p.data ? (
        <span>
          {p.data.external_source && (
            <span title="Імпортовано з 1С" style={{
              marginRight: 6, color: '#2563eb', border: '1px solid var(--badge-blue-bd)',
              borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px',
            }}>I</span>
          )}
          {p.data.document_number}
        </span>
      ) : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 110,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 180,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    {
      colId: 'contractor', headerName: 'Контрагент', flex: 1, minWidth: 180,
      cellRenderer: (p: { data?: ReconciliationAct }) =>
        p.data ? (contractorName[p.data.contractor_id] ?? '…') : '',
    },
    { field: 'period_start', headerName: 'Період з', width: 110 },
    { field: 'period_end', headerName: 'по', width: 110 },
    {
      field: 'closing_balance_our', headerName: 'Баланс (наш)', width: 140,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'closing_balance_their', headerName: 'Баланс (контр.)', width: 150,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value ?? '',
    },
    {
      field: 'discrepancy_amount', headerName: 'Розходження', width: 130,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'state', headerName: 'Стан', width: 130,
      valueFormatter: (p) => p.value ? (STATE_LABEL[p.value] ?? p.value) : '',
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], [orgName, contractorName]);

  // «+ Акт звірки» при «Усі організації» — у дефолтну доступну юрособу.
  const createOrgId = orgId || pickDefaultOrg(orgList.data?.items)?.id || '';
  const canCreate = !!createOrgId && allowedOrgIds.has(createOrgId);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Акти звірки<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          onClick={() => navigate(`/settlements/reconciliation-acts/new?org=${createOrgId}`)}
        >
          + Акт звірки
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 200, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <div style={{ width: 240 }}>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={fltContractor}
            onChange={setFltContractor}
            placeholder="контрагент (усі)"
          />
        </div>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltFrom} onChange={setFltFrom} />
        </div>
        <span style={{ color: 'var(--muted)' }}>—</span>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltTo} onChange={setFltTo} />
        </div>
        <select value={fltState} onChange={(e) => setFltState(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}>
          <option value="">Усі стани</option>
          {Object.entries(STATE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        {(fltContractor || fltFrom || fltTo || fltState) && (
          <button onClick={() => {
            setFltContractor(''); setFltFrom(''); setFltTo(''); setFltState('');
          }}>
            скинути
          </button>
        )}
      </div>
      <InfiniteDocGrid<ReconciliationAct>
        // ч.1316: видаляються лише ЧЕРНЕТКИ (рішення власника 2026-08-19) — «×» у колонці дій.
        deleteRow={{
          remove: (id) => reconciliationActs.remove(id),
          canDelete: (r) => r.state === 'DRAFT' && !r.posted && !r.deletion_mark,
          whyNot: (r) => r.state !== 'DRAFT'
            ? 'Відправлений або узгоджений акт не видаляється — лише чернетка'
            : r.posted ? 'Проведений документ не видаляється — спершу розпровести'
              : 'Документ позначено на видалення',
        }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="reconciliation-acts"
        refreshRef={refreshRef}
        onRowDoubleClicked={(r) => navigate(`/settlements/reconciliation-acts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
      <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '0.5rem', maxWidth: 760 }}>
        Акт створюється зі звіту «Акт звірки (звіт)» кнопкою «зафіксувати акт
        звірки» або тут «+ Акт звірки» (генерація з реєстрів). Далі:
        відправити контрагенту → внести його сальдо → узгодити. Узгоджені акти
        підтягуються в «Інвентаризацію розрахунків» як колонка «Підтверджено».
      </p>
    </div>
  );
}
